import type { DocumentRowMapped } from "@/stores/documents/repository/types";
import type { DocumentWithTags } from "@/domains/documents/components/organisms/DocumentListTable";

function documentTimestamps(doc: DocumentRowMapped) {
  return {
    original_created_at: doc.original_created_at ?? null,
    modified_at: doc.modified_at ?? null,
    document_content_modified_date: doc.document_content_modified_date ?? null,
  };
}

function authorDisplay(doc: DocumentRowMapped) {
  return {
    created_by_name: doc.created_by_name ?? null,
    created_by_avatar_url: doc.created_by_avatar_url ?? null,
    created_by_color: doc.created_by_color ?? null,
  };
}

function documentReview(doc: DocumentRowMapped) {
  return {
    verified_at: doc.verified_at ?? null,
    verified_by_name: doc.verified_by_name ?? null,
    linked_issues: doc.linked_issues ?? [],
  };
}

function documentVersion(doc: DocumentRowMapped) {
  return {
    state: doc.state ?? null,
    version: doc.version ?? null,
    version_group: doc.version_group ?? null,
  };
}

function documentOrigin(doc: DocumentRowMapped) {
  return {
    source: doc.source ?? null,
    container_type: doc.container_type ?? null,
    project_id: doc.project_id ?? null,
  };
}

function documentClassification(doc: DocumentRowMapped) {
  return {
    categories: doc.categories ?? [],
    design_disciplines: doc.design_disciplines ?? [],
    design_stages: doc.design_stages ?? [],
  };
}

/**
 * A repository row (`DocumentRowMapped`, numeric ids) reshaped into the row
 * type `DocumentListTable` renders (`DocumentWithTags`, string ids).
 *
 * One exported copy for every store-less consumer of the table: the Folders
 * view held it privately, and the table's own equivalent for version rows
 * cannot be exported from a component file (react-refresh only allows
 * components as exports there). Anything that fetches rows itself and hands
 * them to the table — the Brief page, the search paths — maps through here,
 * so a column added to the row type reaches every one of them at once
 * (CLAUDE.md, "every row-shape hop must carry it").
 */
export function toDocumentWithTags(doc: DocumentRowMapped): DocumentWithTags {
  return {
    id: String(doc.id),
    filename: doc.filename,
    file_path: doc.file_path,
    building_id: String(doc.building_id),
    parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
    content_type: doc.content_type ?? "",
    size: doc.size ?? 0,
    created_at: doc.created_at,
    created_by: doc.created_by,
    folder_id: doc.folder_id ? String(doc.folder_id) : null,
    ...documentTimestamps(doc),
    ...authorDisplay(doc),
    ...documentReview(doc),
    ...documentVersion(doc),
    ...documentOrigin(doc),
    ...documentClassification(doc),
  };
}
